/**
 * audioResourceAdapter.js — resource-registry adapter for Audio Resources.
 *
 * Same interface as fontResourceAdapter.js/datasetResourceAdapter.js, and like
 * them fully synchronous for the CRUD half (audioStore holds its metadata in
 * Pinia/localStorage; only the bytes live in IndexedDB).
 *
 * isUsed() is the one method that can't reuse the generic property walker: an
 * audio reference is not a widget property, it is a typed action's operand,
 * held per event in node.actions (see ConfigManager) so one widget can play a
 * different clip on `down` than on `up`. It therefore goes through
 * findWidgetsUsingActionResource() instead of isResourceUsed().
 */

import { AUDIO_SYNTH_RESOURCE_TYPE, AUDIO_URL_RESOURCE_TYPE, useAudioStore, validateAudioStreamUrl } from '../audioStore.js'
import { useSynthStore } from '../synthStore.js'
import { findWidgetsUsingActionResource } from '../../utils/resourceUsage.js'
import { base64ToText } from '../../package/base64.js'

/**
 * Package entries always carry this adapter's JSON envelope as text, but a
 * source (GitHub blobs, in particular) may hand it back base64-encoded
 * regardless of its semantic content — decode before parsing either way.
 */
function parseEnvelope(entry) {
  const text = entry.encoding === 'base64' ? base64ToText(entry.content) : entry.content
  return JSON.parse(text)
}

export const audioResourceAdapter = {
  type: 'audio',
  // Declares this adapter packageable and where its files live in a Design
  // Package — same contract as imageResourceAdapter's `packageFolder`; the
  // package layer only checks for this field's presence, never the type.
  packageFolder: 'audio',

  list() {
    return useAudioStore().all
  },
  get(uuid) {
    return useAudioStore().getAudio(uuid)
  },
  remove(uuid) {
    return useAudioStore().removeAudio(uuid)
  },
  duplicate(uuid) {
    return useAudioStore().duplicateAudio(uuid)
  },
  rename(uuid, name) {
    return useAudioStore().updateAudio(uuid, { name })
  },
  isUsed(uuid, layer) {
    return findWidgetsUsingActionResource(layer, uuid).length > 0
  },
  getDisplayName(uuid) {
    const resource = useAudioStore().getAudio(uuid)
    if (resource?.type === AUDIO_SYNTH_RESOURCE_TYPE) {
      return useSynthStore().getSynth(resource.synthId)?.name ?? resource.name ?? null
    }
    return resource?.name ?? null
  },

  // --- Packaging (Design Packages — see docs plan) ---

  /**
   * One local clip = one file in audio/: a JSON envelope, exactly the contract
   * imageResourceAdapter/fontResourceAdapter use — never a bare `audio/x.wav`.
   * The raw bytes alone would lose the resource's whole Designer identity: its
   * `uuid` (what a structured action's `resourceId` references), its display
   * `name`, and its `type`. The uuid in particular has to travel with the
   * bytes so a cold import can remap action operands when it has to mint a new
   * one (see originalUuid()/remapReference() below).
   *
   * DesignPackageBuilder only asks for resources where isUsed() is true, so
   * unreferenced clips stay out of the package (same rule as images/fonts).
   */
  async toPackageFile(uuid) {
    const store = useAudioStore()
    const clip = store.getAudio(uuid)
    if (!clip?.filename || clip.type === AUDIO_URL_RESOURCE_TYPE || clip.type === AUDIO_SYNTH_RESOURCE_TYPE) return null
    const dataUrl = await store.getAudioDataURL(uuid)
    if (!dataUrl) return null
    const mime = /^data:([^;]+);base64,/.exec(dataUrl)?.[1] ?? 'audio/wav'
    const base = clip.filename.replace(/\.[^./]+$/, '')
    return {
      filename: `${base}.audio.json`,
      encoding: 'utf8',
      mime: 'application/json',
      content: JSON.stringify({
        uuid: clip.uuid,
        name: clip.name,
        filename: clip.filename,
        mime,
        type: clip.type,
        originalFilename: clip.originalFilename,
        metadata: clip.metadata,
        data: dataUrl, // bytes, as the same data-URL AudioDB already stores
      }),
    }
  },

  /** Project design.json stores only metadata for used external audio URLs. */
  serializeExternalResources(layer) {
    return useAudioStore().all
      .filter(resource => resource.type === AUDIO_URL_RESOURCE_TYPE && this.isUsed(resource.uuid, layer))
      .map(({ uuid, name, url, mimeType }) => ({ uuid, name, url, ...(mimeType ? { mimeType } : {}) }))
  },

  /** Project files keep only the reference identity, never synth contents. */
  serializeSynthReferences(layer) {
    const synthStore = useSynthStore()
    return useAudioStore().all
      .filter(resource => resource.type === AUDIO_SYNTH_RESOURCE_TYPE && this.isUsed(resource.uuid, layer))
      .map(resource => ({
        uuid: resource.uuid,
        name: synthStore.getSynth(resource.synthId)?.name ?? resource.name,
        synthId: resource.synthId,
      }))
  },

  validateSynthReferences(resources) {
    if (!Array.isArray(resources)) throw new Error('Project synth references must be a list.')
    for (const resource of resources) {
      if (typeof resource?.uuid !== 'string' || !resource.uuid || typeof resource?.name !== 'string' || !resource.name
        || typeof resource?.synthId !== 'string' || !resource.synthId) {
        throw new Error('Project contains an incomplete Audio Resource synth reference.')
      }
    }
  },

  restoreSynthReferences(resources) {
    const store = useAudioStore()
    const created = []
    const remaps = []

    for (const resource of resources) {
      const sameSynth = store.getAudioBySynthId(resource.synthId)
      if (sameSynth) {
        if (sameSynth.uuid !== resource.uuid) remaps.push({ oldUuid: resource.uuid, newUuid: sameSynth.uuid })
        continue
      }

      const uuidCollision = store.getAudio(resource.uuid)
      const uuid = store.addSynthAudioResource({
        ...resource,
        uuid: uuidCollision ? null : resource.uuid,
      })
      created.push(uuid)
      if (uuid !== resource.uuid) remaps.push({ oldUuid: resource.uuid, newUuid: uuid })
    }

    return { created, remaps }
  },

  validateExternalResources(resources) {
    if (!Array.isArray(resources)) throw new Error('Project external audio resources must be a list.')
    for (const resource of resources) {
      if (!resource?.uuid || !resource?.name || typeof resource.url !== 'string') {
        throw new Error('Project contains an incomplete external audio resource.')
      }
      validateAudioStreamUrl(resource.url)
      if (resource.mimeType != null && typeof resource.mimeType !== 'string') {
        throw new Error(`External audio resource "${resource.name}" has an invalid audio type.`)
      }
    }
  },

  restoreExternalResources(resources) {
    const store = useAudioStore()
    const created = []
    const remaps = []

    for (const resource of resources) {
      const sameUrl = store.getAudioByUrl(resource.url)
      if (sameUrl) {
        if (sameUrl.uuid !== resource.uuid) {
          remaps.push({ oldUuid: resource.uuid, newUuid: sameUrl.uuid })
        }
        continue
      }

      const uuidCollision = store.getAudio(resource.uuid)
      const uuid = store.addAudioUrlResource({
        ...resource,
        uuid: uuidCollision ? null : resource.uuid,
      })
      created.push(uuid)
      if (uuid !== resource.uuid) remaps.push({ oldUuid: resource.uuid, newUuid: uuid })
    }

    return { created, remaps }
  },

  /**
   * WAV-specific validation deliberately lives here and nowhere else (see
   * audioStore's note on the future extension boundary) — a later audio kind
   * gets its own adapter/validator rather than loosening this one.
   */
  validatePackageFile(entry) {
    let parsed
    try {
      parsed = parseEnvelope(entry)
    } catch {
      throw new Error(`Audio "${entry.filename}" is not valid`)
    }
    if (!parsed?.filename || !/\.wav$/i.test(parsed.filename)) {
      throw new Error(`Audio "${entry.filename}" is not a .wav file`)
    }
    if (!parsed?.data || !/^data:/.test(parsed.data)) {
      throw new Error(`Audio "${entry.filename}" has no audio data`)
    }
  },

  /**
   * Restores the clip, keeping its original uuid whenever that uuid is still
   * free — so a cold import needs no remapping at all and every structured
   * action's `resourceId` resolves as saved. Deduped on the device filename
   * (the clip's plate-side identity): an existing clip under that name is
   * reused as-is (created: false, so DesignPackageLoader never rolls back a
   * resource that was already in the library). A uuid collision with a
   * *different* clip falls back to a freshly minted uuid, which
   * DesignPackageLoader then remaps via originalUuid()/remapReference().
   */
  async commitPackageFile(entry) {
    const store = useAudioStore()
    const parsed = parseEnvelope(entry)
    const existing = store.getAudioByFilename(parsed.filename)
    if (existing) return { uuid: existing.uuid, created: false }
    const uuid = await store.addImportedClip({
      uuid: parsed.uuid,
      filename: parsed.filename,
      dataUrl: parsed.data,
      name: parsed.name,
      type: parsed.type ?? 'wav',
      mime: parsed.mime ?? 'audio/wav',
      originalFilename: parsed.originalFilename,
      metadata: parsed.metadata,
    })
    return { uuid, created: true }
  },

  /** The uuid this packaged clip was exported under — see toPackageFile()'s comment. */
  originalUuid(entry) {
    try {
      return parseEnvelope(entry)?.uuid ?? null
    } catch {
      return null
    }
  },

  /**
   * Rewrites structured action operands so they still resolve when an import
   * had to mint a new uuid for a packaged clip. Mirrors
   * imageResourceAdapter.remapReference, but walks each object's per-event
   * `actions` handler list rather than a single property, since every event carries its
   * own operand (see ObjectExport / ConfigManager).
   */
  remapReference(objects, oldUuid, newUuid) {
    for (const obj of objects) {
      // Page records carry event/action nodes (design-file form) in `logic`.
      for (const node of obj.logic?.nodes ?? []) {
        if (node?.data?.resourceId === oldUuid) node.data.resourceId = newUuid
      }
      const actions = obj.actions
      if (!actions) continue
      const handlers = Array.isArray(actions)
        ? actions
        : Object.entries(actions).map(([event, entries]) => ({ event, actions: Array.isArray(entries) ? entries : [entries] }))
      for (const handler of handlers) {
        for (const entry of handler.actions ?? []) {
          if (entry?.resourceId === oldUuid) entry.resourceId = newUuid
        }
      }
    }
  },
}
